import React, { useState, useEffect } from "react";
import { Input, Select, Space } from "antd";
import { all as getAllCountries } from "country-codes-list";

// Phone validation for important countries (digits required)
const PHONE_VALIDATION = {
  "+91": 10,   // India
  "+1": 10,    // USA & Canada
  "+966": 9,   // Saudi Arabia
  "+7": 10,    // Russia
  "+86": 11,   // China
  "+44": 10,   // UK
  "+971": 9,   // UAE
  "+61": 9,    // Australia
  "+49": 10,   // Germany
};

// Get required digits for a country (null = use default 6-15)
export const getPhoneDigits = (dialCode) => {
  return PHONE_VALIDATION[dialCode] || null;
};

// Build countries list once (sorted A-Z by country name)
const buildCountriesList = () => {
  try {
    const allCountries = getAllCountries();

    return allCountries
      .filter((country) => country.countryCallingCode)
      .map((country) => ({
        code: country.countryCode,
        name: country.countryNameEn,
        dialCode: `+${country.countryCallingCode}`,
        flag: country.flag || "",
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  } catch (error) {
    console.error("Error loading countries:", error);
    return [{ code: "IN", name: "India", dialCode: "+91", flag: "🇮🇳" }];
  }
};

const COUNTRIES_LIST = buildCountriesList();

const PhoneInputWithCountryCode = ({
  value,
  onChange,
  placeholder = "Enter phone number",
  size = "middle",
  disabled = false,
  style = {},
  inputStyle = {},
  selectStyle = {},
}) => {
  // Internal state
  const [countryCode, setCountryCode] = useState("+91"); // Default India
  const [phoneNumber, setPhoneNumber] = useState("");

  // Sync with external value prop
  useEffect(() => {
    if (value?.countryCode) {
      setCountryCode(value.countryCode);
    }
    if (value?.phoneNumber !== undefined) {
      setPhoneNumber(value.phoneNumber);
    }
  }, [value?.countryCode, value?.phoneNumber]);

  // Trigger change to parent
  const triggerChange = (newCountryCode, newPhoneNumber) => {
    if (onChange) {
      onChange({
        countryCode: newCountryCode,
        phoneNumber: newPhoneNumber,
        fullNumber: `${newCountryCode}${newPhoneNumber}`,
      });
    }
  };

  // Handle country selection
  const handleCountryChange = (selectedDialCode) => {
    setCountryCode(selectedDialCode);
    triggerChange(selectedDialCode, phoneNumber);
  };

  // Handle phone number input
  const handlePhoneChange = (e) => {
    const inputValue = e.target.value;
    // Allow only digits
    const digitsOnly = inputValue.replace(/\D/g, "");
    setPhoneNumber(digitsOnly);
    triggerChange(countryCode, digitsOnly);
  };

  // Find selected country for display
  const selectedCountry = COUNTRIES_LIST.find((c) => c.dialCode === countryCode);

  return (
    <Space.Compact style={{ display: "flex", width: "100%", ...style }}>
      <Select
        value={countryCode}
        onChange={handleCountryChange}
        size={size}
        disabled={disabled}
        style={{
          width: "80px",
          flexShrink: 0,
          ...selectStyle,
        }}
        popupMatchSelectWidth={false}
        listHeight={300}
        optionLabelProp="label"
      >
        {COUNTRIES_LIST.map((country, index) => (
          <Select.Option
            key={`${country.code}-${index}`}
            value={country.dialCode}
            label={country.dialCode}
          >
            <span style={{ marginRight: "6px" }}>{country.flag}</span>
            <span style={{ marginRight: "6px", fontSize: "13px" }}>{country.name}</span>
            <span style={{ color: "#888", fontSize: "12px" }}>({country.dialCode})</span>
          </Select.Option>
        ))}
      </Select>
      <Input
        value={phoneNumber}
        onChange={handlePhoneChange}
        placeholder={placeholder}
        size={size}
        disabled={disabled}
        style={{
          flex: 1,
          ...inputStyle,
        }}
        maxLength={15}
      />
    </Space.Compact>
  );
};

export default PhoneInputWithCountryCode;
